// Every render is a version (v1, v2…): play one, compare two (side by side on desktop, swipe on phones), or restore
// an older one as the latest. Restoring only moves the pointer; nothing is regenerated and nothing is lost.
import { useState } from "react";
import { api, exportLink, type ExportFormat } from "../api";
import { director } from "../director";
import { fmtLength } from "../library";
import { playNow } from "../player";
import { reviewPath } from "../notes"; // v4 (T4)
import { Link } from "../router";
import { bump, toast } from "../store";
import type { Project, Version } from "../types";
import { Icon, Sheet, timeAgo } from "./bits";
import { Download } from "./Download";
import { openShare } from "../share";
import { ShareIcon } from "./ShareSheet";
import { Fold } from "./Fold"; // v4 (V4-P2)
import { versionsSummary } from "../foldtext";

/**
 * v4 (V4-P2): the list is a Fold ("video.versions"), its row saying which version is in use. `defaultOpen` is for
 * the first visit only (the desktop Versions tab opens it; on phones it starts shut under the review tray).
 */
export function Versions({ p, versions, onRestored, defaultOpen = false }: { p: Project; versions: Version[] | null; onRestored: (p: Project) => void; defaultOpen?: boolean }) {
  const [compare, setCompare] = useState<[Version, Version] | null>(null);
  const [picking, setPicking] = useState<Version | null>(null);
  const [restore, setRestore] = useState<Version | null>(null);
  const [exporting, setExporting] = useState<Version | null>(null);
  const [busy, setBusy] = useState(false);
  if (!versions) return null;
  const sorted = [...versions].sort((a, b) => b.version - a.version);

  return (
    <Fold id="video.versions" title="Versions" count={sorted.length || undefined} summary={versionsSummary(sorted)} defaultOpen={defaultOpen} holds={["version.share"]} className="versions">
      {sorted.length > 1 && (
        <p className="ver-hint">
          {picking ? <span>Pick another to compare with v{picking.version}</span> : <span className="muted">Pick two to compare</span>}
          {picking && (
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setPicking(null)}>
              Cancel
            </button>
          )}
        </p>
      )}
      {!sorted.length && <p className="muted">Each render you make is kept here as v1, v2 and so on. Nothing is ever lost when you rework the video.</p>}
      <ol className="ver-list">
        {sorted.map((v) => (
          <li key={v.version} className={`ver${v.isLatest ? " is-latest" : ""}${picking?.version === v.version ? " is-picked" : ""}`}>
            <button data-action="versions.play" type="button" className="ver-thumb" onClick={() => playNow({ url: v.url, title: `${p.title}, v${v.version}`, what: "render", projectId: p.id, version: v.version })} aria-label={`Play version ${v.version}`}>
              {v.thumbUrl || p.thumbUrl ? <img src={v.thumbUrl || p.thumbUrl || ""} alt="" /> : <span />}
              <span className="ver-play" aria-hidden>
                <Icon name="play" size={14} />
              </span>
            </button>
            <div className="ver-text">
              <p className="ver-name">
                v{v.version}
                {v.isLatest && <span className="tag tag-accent">In use</span>}
              </p>
              <p className="muted">
                {fmtLength(v.durationS)}, {timeAgo(v.createdAt)}
              </p>
            </div>
            <div className="ver-acts">
              {sorted.length > 1 && (
                <button
                  data-action="versions.compare"
                  type="button"
                  className={`icon-btn${picking?.version === v.version ? " is-on" : ""}`}
                  aria-label={picking ? `Compare with version ${v.version}` : `Compare version ${v.version} with another`}
                  title="Compare"
                  onClick={() => {
                    if (!picking) return setPicking(v);
                    if (picking.version === v.version) return setPicking(null);
                    setCompare([picking.version < v.version ? picking : v, picking.version < v.version ? v : picking]);
                    setPicking(null);
                  }}
                >
                  <Icon name="compare" size={18} />
                </button>
              )}
              {/* v4 (T4): review this version moment by moment, with notes */}
              <Link data-action="versions.review" to={reviewPath(p.id, { v: v.version })} className="btn btn-quiet btn-small ver-review" aria-label={`Review version ${v.version}`}>
                <Icon name="chat" size={14} /> <span className="ver-review-word">Review</span>
              </Link>
              {/* v4 (V4-SH): a private link to watch this version */}
              <button type="button" className="icon-btn" data-help="version.share" onClick={() => openShare({ projectId: p.id, version: v.version, title: p.title })} aria-label={`Share version ${v.version}`} title="Share">
                <ShareIcon />
              </button>
              <Download url={v.downloadUrl} name={v.fileName} label={`version ${v.version}`} />
              {v.renderId && (
                <button type="button" className="icon-btn" onClick={() => setExporting(v)} aria-label={`Export version ${v.version} to another editor`} title="Export">
                  <Icon name="stack" size={18} />
                </button>
              )}
              {!v.isLatest && (
                <button type="button" className="btn btn-quiet btn-small" onClick={() => setRestore(v)}>
                  <Icon name="restore" size={14} /> Restore
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
      {compare && <CompareSheet p={p} pair={compare} onClose={() => setCompare(null)} />}
      {exporting && <ExportSheet v={exporting} onClose={() => setExporting(null)} />}
      {restore && (
        <Sheet title={`Restore v${restore.version}?`} onClose={() => setRestore(null)}>
          <div className="sheet-body confirm">
            <p>Version {restore.version} becomes the one in use, and what you download and share. The newer versions stay right here. Nothing is regenerated, so it's free.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-quiet" onClick={() => setRestore(null)}>
                Keep the current one
              </button>
              <button
                data-action="versions.restore"
                type="button"
                className="btn btn-primary"
                disabled={busy}
                data-autofocus
                onClick={async () => {
                  setBusy(true);
                  try {
                    const next = await api.restoreVersion(p.id, restore.version);
                    onRestored(next);
                    bump("project", "library");
                    director.context(`The user restored version ${restore.version} of "${p.title}" by tapping. It's the one in use now.`);
                    toast(`Version ${restore.version} is in use again.`, "good");
                    setRestore(null);
                  } catch (e) {
                    toast(`Couldn't restore it. ${(e as Error).message}`, "bad");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Restore v{restore.version}
              </button>
            </div>
          </div>
        </Sheet>
      )}
    </Fold>
  );
}

const EXPORTS: { format: ExportFormat; label: string; note: string }[] = [
  { format: "pack", label: "Project pack (.zip)", note: "The video, every picture, clip and sound, the timelines for each editor, captions and a how-to." },
  { format: "fcpxml", label: "Final Cut / Resolve (.fcpxml)", note: "The edit for Final Cut Pro and DaVinci Resolve." },
  { format: "xmeml", label: "Premiere (.xml)", note: "The edit for Premiere Pro (Final Cut 7 XML; Resolve reads it too)." },
  { format: "otio", label: "OpenTimelineIO (.otio)", note: "The edit for tools that read OpenTimelineIO." },
  { format: "srt", label: "Captions (.srt)", note: "Subtitles, timed to the voice." },
];

/** Export a version to another editor: the timeline files point at media/, so the pack is the one to open in place. */
function ExportSheet({ v, onClose }: { v: Version; onClose: () => void }) {
  const [busy, setBusy] = useState<ExportFormat | null>(null);
  const download = async (format: ExportFormat) => {
    setBusy(format);
    try {
      const { url, filename } = await exportLink(v.renderId, format);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast(format === "pack" ? "Your project pack is downloading." : `${filename} is downloading.`, "good");
      onClose();
    } catch (e) {
      toast(`Couldn't export it. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };
  return (
    <Sheet title={`Export v${v.version}`} onClose={onClose}>
      <div className="sheet-body">
        <p className="sheet-note">Open it in another editor. The timeline files point at the pack's media folder, so for an editable project download the pack and unzip it first.</p>
        {EXPORTS.map((x, i) => (
          <button data-action="download" key={x.format} type="button" className="btn btn-quiet btn-block" title={x.note} disabled={busy !== null} data-autofocus={i === 0 ? true : undefined} onClick={() => void download(x.format)}>
            <Icon name="download" size={14} /> {busy === x.format ? "Preparing..." : x.label}
          </button>
        ))}
        <p className="sheet-note">{EXPORTS[0]!.note}</p>
      </div>
    </Sheet>
  );
}

/** Two versions: side by side on desktop, one at a time with a swipe (scroll snap) on phones. */
function CompareSheet({ p, pair, onClose }: { p: Project; pair: [Version, Version]; onClose: () => void }) {
  const [shown, setShown] = useState(0);
  const aspect = (p.brief.aspect ?? "9:16").replace(":", "x");
  return (
    <Sheet title={`v${pair[0].version} and v${pair[1].version}`} onClose={onClose} wide className="compare-sheet">
      <div className="sheet-body">
        <div
          className="compare"
          onScroll={(e) => {
            const el = e.currentTarget;
            setShown(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
          }}
        >
          {pair.map((v) => (
            <figure key={v.version} className="compare-cell">
              <div className={`compare-media compare-${aspect}`}>
                <video src={v.url} poster={v.thumbUrl || p.thumbUrl || undefined} controls playsInline preload="metadata" />
              </div>
              <figcaption>
                <b>v{v.version}</b> {v.isLatest ? "in use" : timeAgo(v.createdAt)}, {fmtLength(v.durationS)}
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="compare-dots" aria-hidden>
          {pair.map((v, i) => (
            <span key={v.version} className={i === shown ? "is-on" : ""} />
          ))}
        </p>
        <p className="muted compare-hint">Swipe to switch. Each plays with its own sound.</p>
      </div>
    </Sheet>
  );
}
